// PartsPricingHint — gated by `partsPricing`. Suggestion-only labels.
// Phase 3.5: quantity-mode aware suggestion display.
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useAuth } from '@/contexts/AuthContext';
import { useShopPricingConfig, useShopPricingSettings } from '@/hooks/pricing/useShopPricingConfig';
import { suggestedSellHybrid, round2 } from '@/lib/parts-financial/calculator';
import { Badge } from '@/components/ui/badge';

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

interface Props {
  qty: number;
  unitCost: number | null | undefined;
  unitSell: number | null | undefined;
  category?: string | null;
  /** Phase 8 R3 — informational origin tag; does not influence pricing math. */
  sourceType?: string | null;
}

const SOURCE_LABEL: Record<string, string> = {
  flat: 'Flat',
  matrix: 'Matrix',
  optiguard: 'OptiGuard',
  fallback: 'Default matrix',
};

export function PartsPricingHint({ qty, unitCost, unitSell, category, sourceType }: Props) {
  const enabled = useFeatureFlag('partsPricing');
  const hintsEnabled = useExposureFlag('ui_partsPricingHints');
  const matrixEnabled = useFeatureFlag('partsPricingMatrix');
  const { activeShop } = useAuth();
  const { config } = useShopPricingConfig(matrixEnabled ? activeShop?.id : undefined);
  const settingsQ = useShopPricingSettings(activeShop?.id);

  if (!enabled) return null;
  if (!unitCost || unitCost <= 0) return null;

  const cost = Number(unitCost);
  const safeQty = Math.max(1, Number(qty || 1));
  const mode = settingsQ.data?.matrix_quantity_mode === 'grouped' ? 'grouped' : 'per_unit';

  const hybrid = matrixEnabled
    ? suggestedSellHybrid(cost, category ?? null, config, { quantity: safeQty, quantityMode: mode })
    : null;

  const suggestedEach = hybrid?.suggestedEach ?? 0;
  const suggestedTotal = hybrid?.suggestedTotal ?? round2(suggestedEach * safeQty);
  const hasOverride = unitSell != null && Number.isFinite(Number(unitSell));
  const effectiveEach = hasOverride ? Number(unitSell) : suggestedEach;
  const gpDollars = round2((effectiveEach - cost) * safeQty);
  const gpPercent = effectiveEach > 0 ? round2(((effectiveEach - cost) / effectiveEach) * 100) : 0;

  const sourceLabel = hasOverride ? 'Manual override' : SOURCE_LABEL[hybrid?.source ?? 'fallback'] ?? 'Suggested';
  const minGpWarn = Number(settingsQ.data?.min_gp_warning_pct ?? 30);
  const belowGp = effectiveEach > 0 && gpPercent < minGpWarn;

  return (
    <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-1">
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground">Suggested:</span>
          {fmt(suggestedEach)}
          <span className="text-muted-foreground">/ea · total {fmt(suggestedTotal)}</span>
          {hintsEnabled && (
            <Badge variant={hasOverride ? 'secondary' : 'outline'} className="text-[10px] py-0 h-4">
              {sourceLabel}
            </Badge>
          )}
          {hintsEnabled && mode === 'grouped' && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">grouped</Badge>
          )}
          {hintsEnabled && !hasOverride && hybrid?.clampedBy && (
            <Badge variant="outline" className="text-[10px] py-0 h-4">
              clamped: {hybrid.clampedBy}
            </Badge>
          )}
        </span>
        <span>
          <span className="font-medium text-foreground">Est. GP:</span>{' '}
          {fmt(gpDollars)} ({gpPercent.toFixed(1)}%)
          {belowGp && (
            <Badge variant="outline" className="ml-1 text-[10px] py-0 h-4 border-warning text-warning">
              below {minGpWarn}%
            </Badge>
          )}
        </span>
      </div>
      <div className="text-[10px] opacity-70">
        Suggestion only — manual sell override is authoritative when entered.
      </div>
      {sourceType === 'canned_job' && (
        <div className="text-[10px] opacity-70">
          Origin: canned job template (informational)
        </div>
      )}
    </div>
  );
}
